const createCraftingPriorityToggle = () => ({
    enabled: false,
    highestValue: false
})

const MAX_COMMANDER_SELECTION_ID = 350
const MAX_CASTELLAN_SELECTION_ID = 50

export const createDefaultCommandersCastellansCrafting = () => ({
    runRequestedAt: 0,
    commander: {
        enabled: false,
        selectedId: '1',
        effects: {
            playerEffects: false,
            npcEffects: false,
            meleeUnitsStrength: createCraftingPriorityToggle(),
            rangeUnitsStrength: createCraftingPriorityToggle(),
            courtyardStrength: createCraftingPriorityToggle(),
            frontUnitsLimit: createCraftingPriorityToggle(),
            wallUnitsLimit: createCraftingPriorityToggle(),
            travelSpeed: createCraftingPriorityToggle(),
            laterArmyDetection: createCraftingPriorityToggle(),
            frontStrength: createCraftingPriorityToggle(),
            flanksStrength: createCraftingPriorityToggle(),
            universalCombatStrength: createCraftingPriorityToggle(),
            mustHaveShieldMaidenSupportInCourtyard: false,
            mustHaveAdditionalWaves: false
        }
    },
    castellan: {
        enabled: false,
        selectedId: '1',
        effects: {
            playerEffects: false,
            npcEffects: false,
            meleeUnitsStrength: createCraftingPriorityToggle(),
            rangeUnitsStrength: createCraftingPriorityToggle(),
            courtyardStrength: createCraftingPriorityToggle(),
            wallUnitsLimit: createCraftingPriorityToggle(),
            defendingMainCastleStrength: createCraftingPriorityToggle(),
            earlierArmyDetection: createCraftingPriorityToggle(),
            defendingOutpostLandmarkStrength: createCraftingPriorityToggle(),
            frontStrength: createCraftingPriorityToggle(),
            flanksStrength: createCraftingPriorityToggle(),
            universalCombatStrength: createCraftingPriorityToggle(),
            mustHaveShieldMaidenSupportInCourtyard: false
        }
    }
})

export const DEFAULT_ALLIANCE_RIFT_PORTAL_SETTINGS = Object.freeze({
    attackPortal: false,
    wallCheckBeforeImpactSeconds: '15',
    stopAttackingMode: 'own_points',
    stopAtBossPoints: '0',
    activityStopMode: 'off',
    stopAtActivityPoints: '0',
    troopReserves: [],
    onlySendRecallableAttacks: false,
    wallCheckAtCallbackWindow: false,
    callAllAttacksBackWhenStopping: false,
    travelMode: 'fastest_coin_horse',
    useTravelFeathers: false,
    requireShieldMaidenSupport: false,
    commanderRules: [],
    ruleSets: [],
    shopEnabled: false,
    shopCheckIntervalSeconds: '300',
    shopPurchaseLimit: '0',
    shopRubyReserve: '100000',
    shopRiftCoinReserve: '0',
    shopOffers: []
})

export const DEFAULT_GRAND_TOURNAMENT_SETTINGS = Object.freeze({
    swapQuestsEnabled: false,
    rubyMode: 'watch_limit',
    keepRubies: '100000',
    stopAtEnoughToKeep: true,
    keepQuestCount: '3',
    swapQuestIds: [],
    takeQuestsEnabled: false,
    takeQuestIds: [],
    collectFinishedQuests: true,
    buyExtraQuestTicket: false,
    ticketRubyReserve: '100000',
    ticketCheckIntervalSeconds: '300'
})

export const DEFAULT_BOT_SETTINGS = Object.freeze({
    calendarAutomationEnabled: false,
    relogAmount: '1000',
    relogDelaySeconds: '900',
    enableAllianceRiftPortal: false,
    allianceRiftPortal: DEFAULT_ALLIANCE_RIFT_PORTAL_SETTINGS,
    enableGrandTournament: false,
    grandTournament: DEFAULT_GRAND_TOURNAMENT_SETTINGS,
    enableStandbyAutoResume: true,
    sessionHandoffReconnectMinutes: '0',
    minAttackWaitSeconds: '8',
    maxAttackWaitSeconds: '16',
    enableErrorCooldown: true,
    errorThresholdCount: '1',
    errorThresholdMinutes: '45',
    cooldownDurationMinutes: '60',
    commandersCastellansCrafting: createDefaultCommandersCastellansCrafting()
})

const parsePositiveWholeNumber = (value, fallback) => {
    const parsed = Number.parseInt(String(value ?? fallback), 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const parsePositiveDecimal = (value, fallback) => {
    const parsed = Number(String(value ?? fallback))
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const parseBoundedWholeNumber = (value, fallback, min, max) => {
    const parsed = Number.parseInt(String(value ?? fallback), 10)
    const safeValue = Number.isFinite(parsed) ? parsed : fallback
    return Math.min(max, Math.max(min, safeValue))
}

const normalizeQuestIdSelection = value => Array.from(new Set(
    (Array.isArray(value) ? value : [])
        .map(questId => String(questId ?? '').trim())
        .filter(questId => /^\d+$/.test(questId))
)).slice(0, 5000)

const normalizeWallSelection = value => ({
    left: value?.left !== false,
    middle: value?.middle !== false,
    right: value?.right !== false
})

const normalizeRiftCondition = (value, defaultMode) => ({
    enabled: Boolean(value?.enabled),
    mode: ['any_above', 'all_below'].includes(value?.mode) ? value.mode : defaultMode,
    walls: normalizeWallSelection(value?.walls),
    threshold: String(parseBoundedWholeNumber(value?.threshold, 0, 0, 100))
})

const normalizeRiftRule = (value, index = 0) => {
    const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {}
    const setupType = ['preset', 'automatic'].includes(source.setupType) ? source.setupType : 'automatic'
    const unitPriority = list => Array.from(new Set(
        (Array.isArray(list) ? list : []).map(id => String(id ?? '').trim()).filter(id => /^\d+$/.test(id))
    )).slice(0, 100)

    return {
        id: String(source.id ?? `rift-rule-${Date.now()}-${index}`).slice(0, 100),
        enabled: source.enabled !== false,
        name: String(source.name ?? '').trim().slice(0, 120),
        walls: normalizeWallSelection(source.walls),
        portalLifeMin: String(parseBoundedWholeNumber(source.portalLifeMin, 0, 0, 100)),
        portalLifeMax: String(parseBoundedWholeNumber(source.portalLifeMax, 100, 0, 100)),
        setupType,
        presetId: String(source.presetId ?? '').trim().slice(0, 100),
        waves: String(parseBoundedWholeNumber(source.waves, 1, 1, 99)),
        meleeUnitPriority: unitPriority(source.meleeUnitPriority),
        rangedUnitPriority: unitPriority(source.rangedUnitPriority),
        fillCourtyard: Boolean(source.fillCourtyard),
        supportToolIds: unitPriority(source.supportToolIds).slice(0, 3),
        sendCondition: normalizeRiftCondition(source.sendCondition, 'any_above'),
        retreatCondition: normalizeRiftCondition(source.retreatCondition, 'any_above')
    }
}

const normalizeRiftCommanderRules = value => (Array.isArray(value) ? value : [])
    .map((entry, index) => ({
        commanderPosition: String(parseBoundedWholeNumber(entry?.commanderPosition, index + 1, 1, 350)),
        rules: (Array.isArray(entry?.rules) ? entry.rules : []).slice(0, 100).map(normalizeRiftRule)
    }))
    .filter((entry, index, list) => list.findIndex(candidate => candidate.commanderPosition === entry.commanderPosition) === index)
    .slice(0, 350)

const normalizeRiftRuleSets = value => (Array.isArray(value) ? value : []).slice(0, 100).map((entry, index) => ({
    id: String(entry?.id ?? `rift-ruleset-${Date.now()}-${index}`).slice(0, 100),
    name: String(entry?.name ?? `Rule set ${index + 1}`).trim().slice(0, 120),
    rules: (Array.isArray(entry?.rules) ? entry.rules : []).slice(0, 100).map(normalizeRiftRule)
}))

const normalizeRiftShopOffers = value => (Array.isArray(value) ? value : []).slice(0, 500).map((entry, index) => ({
    id: String(entry?.id ?? entry?.packageId ?? `rift-offer-${index}`).slice(0, 100),
    packageId: String(entry?.packageId ?? entry?.id ?? '').trim().slice(0, 100),
    name: String(entry?.name ?? '').trim().slice(0, 160),
    currencyId: String(entry?.currencyId ?? '').trim().slice(0, 20),
    kind: ['tool', 'chest', 'token', 'other'].includes(entry?.kind) ? entry.kind : 'other',
    amount: String(parseBoundedWholeNumber(entry?.amount, 1, 1, 999999999)),
    stockLimit: String(parseBoundedWholeNumber(entry?.stockLimit, 0, 0, 999999999))
}))

export const normalizeAllianceRiftPortalSettings = (value, legacyEnabled = false) => {
    const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {}
    const stopAttackingMode = ['own_points', 'boss_points', 'never'].includes(source.stopAttackingMode) ?
        source.stopAttackingMode :
        DEFAULT_ALLIANCE_RIFT_PORTAL_SETTINGS.stopAttackingMode
    const travelMode = ['fastest_coin_horse', 'standard_coin_horse', 'no_horse', 'fast_travel_feathers'].includes(source.travelMode) ?
        source.travelMode :
        DEFAULT_ALLIANCE_RIFT_PORTAL_SETTINGS.travelMode
    return {
        attackPortal: source.attackPortal === undefined ? Boolean(legacyEnabled) : Boolean(source.attackPortal),
        wallCheckBeforeImpactSeconds: String(parseBoundedWholeNumber(
            source.wallCheckBeforeImpactSeconds,
            Number(DEFAULT_ALLIANCE_RIFT_PORTAL_SETTINGS.wallCheckBeforeImpactSeconds),
            1,
            300
        )),
        stopAttackingMode,
        stopAtBossPoints: String(parseBoundedWholeNumber(source.stopAtBossPoints, 0, 0, 999999999)),
        activityStopMode: ['off', 'all_rewards', 'own_points'].includes(source.activityStopMode) ? source.activityStopMode : 'off',
        stopAtActivityPoints: String(parseBoundedWholeNumber(source.stopAtActivityPoints, 0, 0, 999999999)),
        troopReserves: (Array.isArray(source.troopReserves) ? source.troopReserves : []).slice(0, 500)
            .filter(entry => entry?.unitID === '' || (Number.isInteger(Number(entry?.unitID)) && Number(entry.unitID) > 0))
            .map(entry => ({ unitID: String(entry.unitID), amount: String(parseBoundedWholeNumber(entry.amount, 0, 0, 999999999)) })),
        onlySendRecallableAttacks: Boolean(source.onlySendRecallableAttacks),
        wallCheckAtCallbackWindow: Boolean(source.wallCheckAtCallbackWindow),
        callAllAttacksBackWhenStopping: Boolean(source.callAllAttacksBackWhenStopping),
        travelMode,
        useTravelFeathers: travelMode === 'fast_travel_feathers' || Boolean(source.useTravelFeathers),
        requireShieldMaidenSupport: Boolean(source.requireShieldMaidenSupport),
        commanderRules: normalizeRiftCommanderRules(source.commanderRules),
        ruleSets: normalizeRiftRuleSets(source.ruleSets),
        shopEnabled: Boolean(source.shopEnabled),
        shopCheckIntervalSeconds: String(parseBoundedWholeNumber(
            source.shopCheckIntervalSeconds,
            Number(DEFAULT_ALLIANCE_RIFT_PORTAL_SETTINGS.shopCheckIntervalSeconds),
            30,
            86400
        )),
        shopPurchaseLimit: String(parseBoundedWholeNumber(source.shopPurchaseLimit, 0, 0, 999999999)),
        shopRubyReserve: String(parseBoundedWholeNumber(source.shopRubyReserve, 100000, 0, 999999999)),
        shopRiftCoinReserve: String(parseBoundedWholeNumber(source.shopRiftCoinReserve, 0, 0, 999999999)),
        shopOffers: normalizeRiftShopOffers(source.shopOffers)
    }
}

export const normalizeGrandTournamentSettings = value => {
    const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {}
    return {
        swapQuestsEnabled: Boolean(source.swapQuestsEnabled),
        rubyMode: ['wait_free', 'watch_limit'].includes(source.rubyMode) ? source.rubyMode : DEFAULT_GRAND_TOURNAMENT_SETTINGS.rubyMode,
        keepRubies: String(parseBoundedWholeNumber(source.keepRubies, 100000, 0, 999999999)),
        stopAtEnoughToKeep: source.stopAtEnoughToKeep !== false,
        keepQuestCount: String(parseBoundedWholeNumber(source.keepQuestCount, 3, 1, 24)),
        swapQuestIds: normalizeQuestIdSelection(source.swapQuestIds),
        takeQuestsEnabled: Boolean(source.takeQuestsEnabled),
        takeQuestIds: normalizeQuestIdSelection(source.takeQuestIds),
        collectFinishedQuests: source.collectFinishedQuests !== false,
        buyExtraQuestTicket: Boolean(source.buyExtraQuestTicket),
        ticketRubyReserve: String(parseBoundedWholeNumber(source.ticketRubyReserve, 100000, 0, 999999999)),
        ticketCheckIntervalSeconds: String(parseBoundedWholeNumber(source.ticketCheckIntervalSeconds, 300, 60, 86400))
    }
}

const normalizeAttackWaitRange = (minValue, maxValue) => {
    const safeFloorSeconds = 6
    const defaultMinSeconds = parsePositiveDecimal(DEFAULT_BOT_SETTINGS.minAttackWaitSeconds, 8)
    const defaultMaxSeconds = parsePositiveDecimal(DEFAULT_BOT_SETTINGS.maxAttackWaitSeconds, 16)
    const parsedMin = parsePositiveDecimal(minValue, defaultMinSeconds)
    const parsedMax = parsePositiveDecimal(maxValue, defaultMaxSeconds)
    const normalizedMin = Math.max(safeFloorSeconds, Math.min(parsedMin, parsedMax))
    const normalizedMax = Math.max(normalizedMin + 2, parsedMax)

    return {
        minAttackWaitSeconds: String(normalizedMin),
        maxAttackWaitSeconds: String(normalizedMax)
    }
}

const normalizeCraftingPriorityToggle = value => ({
    enabled: Boolean(value?.enabled),
    highestValue: Boolean(value?.highestValue)
})

const normalizeLordSelectionId = (value, fallback = '1', max = MAX_CASTELLAN_SELECTION_ID) => {
    const parsedValue = Number.parseInt(String(value ?? fallback), 10)

    return Number.isFinite(parsedValue) && parsedValue >= 1 && parsedValue <= max ?
        String(parsedValue) :
        fallback
}

const normalizeRunRequestedAt = value => {
    const parsedValue = Number(value)
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0
}

export const normalizeCommandersCastellansCrafting = value => {
    let rawValue = value

    if (typeof rawValue === 'string') {
        try {
            rawValue = JSON.parse(rawValue)
        }
        catch {
            rawValue = {}
        }
    }

    rawValue = rawValue && typeof rawValue === 'object' ? rawValue : {}

    const defaults = createDefaultCommandersCastellansCrafting()
    const commanderSource = rawValue.commander && typeof rawValue.commander === 'object' ? rawValue.commander : {}
    const castellanSource = rawValue.castellan && typeof rawValue.castellan === 'object' ? rawValue.castellan : {}
    const commanderEffects = commanderSource.effects && typeof commanderSource.effects === 'object' ? commanderSource.effects : {}
    const castellanEffects = castellanSource.effects && typeof castellanSource.effects === 'object' ? castellanSource.effects : {}

    return {
        runRequestedAt: normalizeRunRequestedAt(rawValue.runRequestedAt),
        commander: {
            enabled: Boolean(commanderSource.enabled),
            selectedId: normalizeLordSelectionId(
                commanderSource.selectedId,
                defaults.commander.selectedId,
                MAX_COMMANDER_SELECTION_ID
            ),
            effects: {
                playerEffects: Boolean(commanderEffects.playerEffects),
                npcEffects: Boolean(commanderEffects.npcEffects),
                meleeUnitsStrength: normalizeCraftingPriorityToggle(commanderEffects.meleeUnitsStrength),
                rangeUnitsStrength: normalizeCraftingPriorityToggle(commanderEffects.rangeUnitsStrength),
                courtyardStrength: normalizeCraftingPriorityToggle(commanderEffects.courtyardStrength),
                frontUnitsLimit: normalizeCraftingPriorityToggle(commanderEffects.frontUnitsLimit),
                wallUnitsLimit: normalizeCraftingPriorityToggle(commanderEffects.wallUnitsLimit),
                travelSpeed: normalizeCraftingPriorityToggle(commanderEffects.travelSpeed),
                laterArmyDetection: normalizeCraftingPriorityToggle(commanderEffects.laterArmyDetection),
                frontStrength: normalizeCraftingPriorityToggle(commanderEffects.frontStrength),
                flanksStrength: normalizeCraftingPriorityToggle(commanderEffects.flanksStrength),
                universalCombatStrength: normalizeCraftingPriorityToggle(commanderEffects.universalCombatStrength),
                mustHaveShieldMaidenSupportInCourtyard: Boolean(commanderEffects.mustHaveShieldMaidenSupportInCourtyard),
                mustHaveAdditionalWaves: Boolean(commanderEffects.mustHaveAdditionalWaves)
            }
        },
        castellan: {
            enabled: Boolean(castellanSource.enabled),
            selectedId: normalizeLordSelectionId(
                castellanSource.selectedId,
                defaults.castellan.selectedId,
                MAX_CASTELLAN_SELECTION_ID
            ),
            effects: {
                playerEffects: Boolean(castellanEffects.playerEffects),
                npcEffects: Boolean(castellanEffects.npcEffects),
                meleeUnitsStrength: normalizeCraftingPriorityToggle(castellanEffects.meleeUnitsStrength),
                rangeUnitsStrength: normalizeCraftingPriorityToggle(castellanEffects.rangeUnitsStrength),
                courtyardStrength: normalizeCraftingPriorityToggle(castellanEffects.courtyardStrength),
                wallUnitsLimit: normalizeCraftingPriorityToggle(castellanEffects.wallUnitsLimit),
                defendingMainCastleStrength: normalizeCraftingPriorityToggle(castellanEffects.defendingMainCastleStrength),
                earlierArmyDetection: normalizeCraftingPriorityToggle(castellanEffects.earlierArmyDetection),
                defendingOutpostLandmarkStrength: normalizeCraftingPriorityToggle(castellanEffects.defendingOutpostLandmarkStrength),
                frontStrength: normalizeCraftingPriorityToggle(castellanEffects.frontStrength),
                flanksStrength: normalizeCraftingPriorityToggle(castellanEffects.flanksStrength),
                universalCombatStrength: normalizeCraftingPriorityToggle(castellanEffects.universalCombatStrength),
                mustHaveShieldMaidenSupportInCourtyard: Boolean(castellanEffects.mustHaveShieldMaidenSupportInCourtyard)
            }
        }
    }
}

export const normalizeBotSettings = value => {
    let rawValue = value

    if (typeof rawValue === 'string') {
        try {
            rawValue = JSON.parse(rawValue)
        }
        catch {
            rawValue = {}
        }
    }

    rawValue = rawValue && typeof rawValue === 'object' ? rawValue : {}

    const normalizedAttackWaitRange = normalizeAttackWaitRange(
        rawValue.minAttackWaitSeconds,
        rawValue.maxAttackWaitSeconds
    )
    const normalizedRelogAmount = Math.max(1, parsePositiveWholeNumber(rawValue.relogAmount, Number(DEFAULT_BOT_SETTINGS.relogAmount)))
    const normalizedRelogDelaySeconds = Math.max(300, parsePositiveWholeNumber(
        rawValue.relogDelaySeconds ?? rawValue.relogDelay,
        Number(DEFAULT_BOT_SETTINGS.relogDelaySeconds)
    ))
    const normalizedSessionHandoffReconnectMinutes = Math.max(0, Number.parseInt(String(
        rawValue.sessionHandoffReconnectMinutes ?? DEFAULT_BOT_SETTINGS.sessionHandoffReconnectMinutes
    ), 10) || 0)
    const normalizedErrorThresholdCount = Math.max(1, parsePositiveWholeNumber(
        rawValue.errorThresholdCount,
        Number(DEFAULT_BOT_SETTINGS.errorThresholdCount)
    ))
    const normalizedErrorThresholdMinutes = Math.max(30, parsePositiveWholeNumber(
        rawValue.errorThresholdMinutes,
        Number(DEFAULT_BOT_SETTINGS.errorThresholdMinutes)
    ))
    const normalizedCooldownDurationMinutes = Math.max(30, parsePositiveWholeNumber(
        rawValue.cooldownDurationMinutes,
        Number(DEFAULT_BOT_SETTINGS.cooldownDurationMinutes)
    ))
    const normalizedAllianceRiftPortal = normalizeAllianceRiftPortalSettings(
        rawValue.allianceRiftPortal,
        rawValue.enableAllianceRiftPortal
    )
    const normalizedGrandTournament = normalizeGrandTournamentSettings(rawValue.grandTournament)

    return {
        calendarAutomationEnabled: rawValue.calendarAutomationEnabled === true,
      relogAmount: String(normalizedRelogAmount),
        relogDelaySeconds: String(normalizedRelogDelaySeconds),
        enableAllianceRiftPortal: rawValue.enableAllianceRiftPortal === undefined ?
            normalizedAllianceRiftPortal.attackPortal :
            Boolean(rawValue.enableAllianceRiftPortal),
        allianceRiftPortal: normalizedAllianceRiftPortal,
        enableGrandTournament: Boolean(rawValue.enableGrandTournament),
        grandTournament: normalizedGrandTournament,
        enableStandbyAutoResume: rawValue.enableStandbyAutoResume !== false,
        sessionHandoffReconnectMinutes: String(normalizedSessionHandoffReconnectMinutes),
        minAttackWaitSeconds: normalizedAttackWaitRange.minAttackWaitSeconds,
        maxAttackWaitSeconds: normalizedAttackWaitRange.maxAttackWaitSeconds,
        enableErrorCooldown: rawValue.enableErrorCooldown !== false,
        errorThresholdCount: String(normalizedErrorThresholdCount),
        errorThresholdMinutes: String(normalizedErrorThresholdMinutes),
        cooldownDurationMinutes: String(normalizedCooldownDurationMinutes),
        commandersCastellansCrafting: normalizeCommandersCastellansCrafting(rawValue.commandersCastellansCrafting)
    }
}
